import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  LogOut,
  Trash2,
  Plus,
  Package,
  ShoppingCart,
  Image as ImageIcon,
  ShieldAlert,
  Check,
  KeyRound,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { api, eur, formatApiError } from "../lib/api";

const TABS = [
  { id: "products", label: "Produkte", icon: Package },
  { id: "orders", label: "Bestellungen", icon: ShoppingCart },
  { id: "gallery", label: "Galerie", icon: ImageIcon },
  { id: "revocations", label: "Widerrufe", icon: ShieldAlert },
];

export default function AdminDashboardPage() {
  const { admin, logout } = useAdminAuth();
  const [tab, setTab] = useState("products");
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    document.title = "Admin · Die Lichtmalerin";
  }, []);

  if (admin === undefined)
    return (
      <div className="min-h-screen bg-[#090909] flex items-center justify-center text-[#A1A1AA] font-sans">
        …
      </div>
    );
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-[#D4AF37]/10 px-6 lg:px-12 py-5 flex items-center justify-between">
        <div>
          <p className="font-serif italic text-xl text-[#E5E5E5]">
            Atelier · Nicole
          </p>
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans mt-1">
            {admin.email}
          </p>
        </div>
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          >
            Zur Website
          </Link>
          <button
            onClick={() => setShowPwd(true)}
            data-testid="admin-change-password"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          >
            <KeyRound size={14} strokeWidth={1.4} /> Passwort
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-red-400 transition-colors"
          >
            <LogOut size={14} strokeWidth={1.4} /> Abmelden
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-6 lg:px-12 py-4 border-b border-[#D4AF37]/10 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light border transition-all ${
                active
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-[#D4AF37]/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
              }`}
            >
              <Icon size={13} strokeWidth={1.4} /> {t.label}
            </button>
          );
        })}
      </div>

      <main className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "revocations" && <RevocationsTab />}
      </main>

      {showPwd && <PasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}

// -------------------------------------------------------------------------
// PASSWORD MODAL
// -------------------------------------------------------------------------
function PasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("Neues Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (next !== confirm) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm p-6 flex items-center justify-center"
      data-testid="admin-password-modal"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#0b0b0b] border border-[#D4AF37]/25 p-8 space-y-5"
      >
        <div>
          <span className="gold-divider">Sicherheit</span>
          <h3 className="font-serif italic text-2xl text-[#E5E5E5] mt-4">
            Passwort ändern
          </h3>
          <p className="mt-3 text-xs font-sans text-[#A1A1AA] leading-relaxed">
            Mindestens 8 Zeichen. Notiere dir das neue Passwort — es lässt
            sich nur mit dem alten Passwort ändern.
          </p>
        </div>

        {ok ? (
          <div
            className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-6 text-center"
            data-testid="password-success"
          >
            <p className="font-serif italic text-[#D4AF37] text-lg">
              Passwort geändert.
            </p>
          </div>
        ) : (
          <>
            <TInput
              label="Aktuelles Passwort"
              testid="pwd-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
            <TInput
              label="Neues Passwort"
              testid="pwd-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
            <TInput
              label="Neues Passwort bestätigen"
              testid="pwd-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />

            {error && (
              <p
                className="text-sm text-red-400 font-sans"
                data-testid="pwd-error"
              >
                {error}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-[#D4AF37]/10">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-[#E5E5E5]"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="pwd-submit"
                className="btn-gold px-8 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light disabled:opacity-40"
              >
                {saving ? "…" : "Ändern"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

// -------------------------------------------------------------------------
// PRODUCTS TAB
// -------------------------------------------------------------------------
const emptyProduct = {
  title: "",
  subtitle: "",
  description: "",
  price_eur: 0,
  shipping_eur: 0,
  stock: 1,
  category: "print",
  image_base64: "",
  active: true,
};

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | product | 'new'
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/products?include_inactive=true")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!confirm("Produkt wirklich löschen?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif italic text-2xl text-[#E5E5E5]">Produkte</h2>
        <button
          onClick={() => setEditing("new")}
          data-testid="admin-new-product"
          className="btn-gold inline-flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light"
        >
          <Plus size={13} strokeWidth={1.4} /> Neu
        </button>
      </div>

      {loading ? (
        <p className="text-[#A1A1AA] font-sans">Lade…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm font-sans">
            <thead className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
              <tr className="border-b border-[#D4AF37]/10">
                <th className="text-left py-3 pr-4">Titel</th>
                <th className="text-right py-3 pr-4">Preis</th>
                <th className="text-right py-3 pr-4">Versand</th>
                <th className="text-right py-3 pr-4">Lager</th>
                <th className="text-center py-3 pr-4">Aktiv</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`admin-product-row-${p.id}`}
                  className="border-b border-[#D4AF37]/5 text-[#E5E5E5]/85"
                >
                  <td className="py-3 pr-4">
                    <p className="font-serif italic text-base text-[#E5E5E5]">
                      {p.title}
                    </p>
                    {p.subtitle && (
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
                        {p.subtitle}
                      </p>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right text-[#D4AF37] font-serif">
                    {eur(p.price_eur)}
                  </td>
                  <td className="py-3 pr-4 text-right">{eur(p.shipping_eur)}</td>
                  <td className="py-3 pr-4 text-right">{p.stock}</td>
                  <td className="py-3 pr-4 text-center">
                    {p.active ? (
                      <span className="text-[#D4AF37]">●</span>
                    ) : (
                      <span className="text-[#A1A1AA]">○</span>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(p)}
                      className="text-[11px] uppercase tracking-[0.3em] text-[#A1A1AA] hover:text-[#D4AF37] mr-4"
                      data-testid={`admin-product-edit-${p.id}`}
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => remove(p.id)}
                      className="text-[#A1A1AA] hover:text-red-400"
                      data-testid={`admin-product-delete-${p.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.4} />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-[#A1A1AA]">
                    Noch keine Produkte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProductEditor
          initial={editing === "new" ? emptyProduct : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProductEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyProduct, ...initial });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial.id);

  const set = (k) => (e) => {
    const v =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.type === "number"
          ? Number(e.target.value)
          : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError("Bild darf max. 4 MB groß sein.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setForm((f) => ({ ...f, image_base64: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        description: form.description,
        price_eur: Number(form.price_eur),
        shipping_eur: Number(form.shipping_eur),
        stock: Number(form.stock),
        category: form.category,
        image_base64: form.image_base64 || null,
        active: form.active,
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.id}`, payload);
      } else {
        await api.post(`/admin/products`, payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto p-6">
      <div
        className="max-w-2xl mx-auto bg-[#0b0b0b] border border-[#D4AF37]/25 p-6 md:p-8 space-y-4"
        data-testid="admin-product-editor"
      >
        <h3 className="font-serif italic text-2xl text-[#E5E5E5] mb-2">
          {isEdit ? "Produkt bearbeiten" : "Neues Produkt"}
        </h3>

        <TInput label="Titel" testid="editor-title" value={form.title} onChange={set("title")} />
        <TInput label="Untertitel" testid="editor-subtitle" value={form.subtitle} onChange={set("subtitle")} />
        <TArea label="Beschreibung" testid="editor-description" value={form.description} onChange={set("description")} />

        <div className="grid grid-cols-3 gap-3">
          <TInput label="Preis (€)" testid="editor-price" type="number" step="0.01" value={form.price_eur} onChange={set("price_eur")} />
          <TInput label="Versand (€)" testid="editor-shipping" type="number" step="0.01" value={form.shipping_eur} onChange={set("shipping_eur")} />
          <TInput label="Lager" testid="editor-stock" type="number" value={form.stock} onChange={set("stock")} />
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">Kategorie</span>
          <select
            value={form.category}
            onChange={set("category")}
            data-testid="editor-category"
            className="mt-1 w-full bg-[#0b0b0b] border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value="print" className="bg-[#0b0b0b] text-[#E5E5E5]">Print / Fotoabzug</option>
            <option value="merchandise" className="bg-[#0b0b0b] text-[#E5E5E5]">Merchandise</option>
            <option value="coaching" className="bg-[#0b0b0b] text-[#E5E5E5]">Coaching</option>
            <option value="other" className="bg-[#0b0b0b] text-[#E5E5E5]">Sonstiges</option>
          </select>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">Bild (max 4 MB)</span>
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            data-testid="editor-image"
            className="mt-2 w-full text-sm font-sans text-[#E5E5E5]/80 file:mr-3 file:py-2 file:px-4 file:border file:border-[#D4AF37]/30 file:bg-transparent file:text-[#D4AF37] file:text-xs file:uppercase file:tracking-widest"
          />
          {form.image_base64 && (
            <img
              src={form.image_base64}
              alt="preview"
              className="mt-3 w-32 h-40 object-cover border border-[#D4AF37]/15"
            />
          )}
        </label>

        <label className="inline-flex items-center gap-3 text-sm text-[#E5E5E5]/85 font-sans">
          <input
            type="checkbox"
            checked={form.active}
            onChange={set("active")}
            data-testid="editor-active"
            className="accent-[#D4AF37]"
          />
          Im Shop sichtbar
        </label>

        {error && <p className="text-sm text-red-400 font-sans">{error}</p>}

        <div className="flex justify-end gap-3 pt-4 border-t border-[#D4AF37]/10">
          <button
            onClick={onClose}
            className="px-6 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-[#E5E5E5]"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="editor-save"
            className="btn-gold px-8 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light disabled:opacity-40"
          >
            {saving ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// ORDERS TAB
// -------------------------------------------------------------------------
function OrdersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/admin/orders")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h2 className="font-serif italic text-2xl text-[#E5E5E5] mb-6">
        Bestellungen
      </h2>
      {loading ? (
        <p className="text-[#A1A1AA] font-sans">Lade…</p>
      ) : items.length === 0 ? (
        <p className="text-[#A1A1AA] font-sans">Noch keine Bestellungen.</p>
      ) : (
        <div className="space-y-4">
          {items.map((o) => (
            <div
              key={o.id}
              data-testid={`admin-order-${o.order_number}`}
              className="border border-[#D4AF37]/12 bg-[#0b0b0b] p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-xl text-[#D4AF37] tabular-nums">
                    {o.order_number}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-sans mt-1">
                    {new Date(o.created_at).toLocaleString("de-DE")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-lg text-[#E5E5E5]">
                    {eur(o.total_eur)}
                  </p>
                  <StatusPill status={o.status} paid={o.payment_status} />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-sans">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
                    Kunde
                  </p>
                  <p className="text-[#E5E5E5] mt-1">{o.customer.name}</p>
                  <p className="text-[#E5E5E5]/70">{o.customer.email}</p>
                  {o.customer.phone && (
                    <p className="text-[#E5E5E5]/70">{o.customer.phone}</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
                    Lieferadresse
                  </p>
                  <p className="text-[#E5E5E5] mt-1">{o.customer.address_line1}</p>
                  {o.customer.address_line2 && (
                    <p className="text-[#E5E5E5]/70">{o.customer.address_line2}</p>
                  )}
                  <p className="text-[#E5E5E5]/70">
                    {o.customer.postal_code} {o.customer.city},{" "}
                    {o.customer.country}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-[#D4AF37]/10">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">
                  Positionen
                </p>
                <ul className="text-sm font-sans text-[#E5E5E5]/85 space-y-1">
                  {o.items.map((line, i) => (
                    <li key={i} className="flex justify-between">
                      <span>
                        {line.quantity}× {line.title}
                      </span>
                      <span className="text-[#D4AF37]">
                        {eur(line.unit_price_eur * line.quantity)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, paid }) {
  let text = status;
  let color = "text-[#A1A1AA] border-[#A1A1AA]/30";
  if (status === "paid") {
    text = "bezahlt";
    color = "text-[#D4AF37] border-[#D4AF37]/50";
  } else if (status === "revocation_requested") {
    text = "Widerruf";
    color = "text-red-400 border-red-400/40";
  } else if (status === "revoked") {
    text = "widerrufen";
    color = "text-red-400 border-red-400/40";
  } else if (status === "pending" || paid === "unpaid") {
    text = "offen";
    color = "text-[#FDE047] border-[#FDE047]/40";
  }
  return (
    <span
      className={`inline-block mt-2 px-3 py-1 text-[9px] uppercase tracking-[0.3em] font-sans font-light border ${color}`}
    >
      {text}
    </span>
  );
}

// -------------------------------------------------------------------------
// GALLERY TAB
// -------------------------------------------------------------------------
function GalleryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [category, setCategory] = useState("spiritual");
  const [image, setImage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/gallery")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError("Bild max. 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(String(reader.result));
    reader.readAsDataURL(file);
  };

  const add = async () => {
    if (!title || !image) {
      setError("Titel und Bild sind erforderlich");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await api.post("/admin/gallery", {
        title,
        year,
        category,
        image_base64: image,
      });
      setTitle("");
      setYear("");
      setCategory("spiritual");
      setImage("");
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Werk löschen?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  const CAT_LABELS = {
    weather: "Wetter",
    forest: "Wald",
    spiritual: "Spirituell",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1 border border-[#D4AF37]/15 bg-[#0b0b0b] p-6 space-y-4 h-fit">
        <h3 className="font-serif italic text-xl text-[#E5E5E5]">
          Werk hinzufügen
        </h3>
        <TInput label="Titel" testid="gallery-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TInput label="Jahr" testid="gallery-year" value={year} onChange={(e) => setYear(e.target.value)} />
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
            Rubrik
          </span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="gallery-category"
            className="mt-1 w-full bg-[#0b0b0b] border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value="spiritual">Spirituelle Fotografie</option>
            <option value="weather">Wetterfotografie</option>
            <option value="forest">Waldfotografie</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
            Bild
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            data-testid="gallery-file"
            className="mt-2 w-full text-sm font-sans text-[#E5E5E5]/80 file:mr-3 file:py-2 file:px-4 file:border file:border-[#D4AF37]/30 file:bg-transparent file:text-[#D4AF37] file:text-xs file:uppercase file:tracking-widest"
          />
          {image && (
            <img
              src={image}
              alt="preview"
              className="mt-3 w-full aspect-[4/5] object-cover border border-[#D4AF37]/15"
            />
          )}
        </label>
        {error && <p className="text-sm text-red-400 font-sans">{error}</p>}
        <button
          onClick={add}
          disabled={saving}
          data-testid="gallery-add"
          className="btn-gold w-full px-6 py-3 text-[11px] uppercase tracking-[0.3em] font-sans font-light disabled:opacity-40"
        >
          {saving ? "…" : "Hinzufügen"}
        </button>
      </div>

      <div className="lg:col-span-2">
        <h3 className="font-serif italic text-xl text-[#E5E5E5] mb-4">
          Aktuelle Werke ({items.length})
        </h3>
        {loading ? (
          <p className="text-[#A1A1AA] font-sans">Lade…</p>
        ) : items.length === 0 ? (
          <p className="text-[#A1A1AA] font-sans">
            Noch keine Werke — die Startseite zeigt Platzhalter.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((g) => (
              <div
                key={g.id}
                data-testid={`gallery-tile-${g.id}`}
                className="group relative border border-[#D4AF37]/12 overflow-hidden"
              >
                <img
                  src={g.image_base64}
                  alt={g.title}
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                  <p className="font-serif italic text-sm text-[#E5E5E5]">
                    {g.title}
                  </p>
                  <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/70">
                    {CAT_LABELS[g.category] || "Spirituell"}
                    {g.year && ` · ${g.year}`}
                  </p>
                </div>
                <button
                  onClick={() => remove(g.id)}
                  data-testid={`gallery-delete-${g.id}`}
                  className="absolute top-2 right-2 p-2 bg-black/70 text-[#A1A1AA] hover:text-red-400"
                >
                  <Trash2 size={14} strokeWidth={1.4} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// REVOCATIONS TAB
// -------------------------------------------------------------------------
function RevocationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/admin/revocations")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const markHandled = async (order_id) => {
    await api.post(`/admin/revocations/${order_id}/handle`);
    load();
  };

  return (
    <div>
      <h2 className="font-serif italic text-2xl text-[#E5E5E5] mb-6 flex items-center gap-3">
        Widerrufe
        <span className="text-[10px] uppercase tracking-[0.3em] text-red-400/80 border border-red-400/30 px-2 py-1">
          {items.filter((o) => !o.revocation?.handled).length} offen
        </span>
      </h2>
      {loading ? (
        <p className="text-[#A1A1AA] font-sans">Lade…</p>
      ) : items.length === 0 ? (
        <p className="text-[#A1A1AA] font-sans">Keine Widerrufe.</p>
      ) : (
        <div className="space-y-4">
          {items.map((o) => (
            <div
              key={o.id}
              data-testid={`admin-revocation-${o.order_number}`}
              className={`border p-5 ${
                o.revocation?.handled
                  ? "border-[#D4AF37]/10 bg-[#0b0b0b]/50 opacity-70"
                  : "border-red-400/30 bg-[#150b0b]"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-xl text-[#D4AF37]">
                    {o.order_number}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-sans mt-1">
                    Eingegangen{" "}
                    {new Date(o.revocation.requested_at).toLocaleString("de-DE")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-lg text-[#E5E5E5]">
                    {eur(o.total_eur)}
                  </p>
                  <p className="text-xs font-sans text-[#E5E5E5]/70">
                    {o.customer.name} · {o.customer.email}
                  </p>
                </div>
              </div>
              {o.revocation.reason && (
                <p className="mt-4 text-sm font-sans italic text-[#E5E5E5]/80 border-l-2 border-[#D4AF37]/40 pl-4">
                  „{o.revocation.reason}“
                </p>
              )}
              <div className="mt-4 pt-3 border-t border-[#D4AF37]/10 flex justify-end">
                {o.revocation.handled ? (
                  <p className="text-xs uppercase tracking-[0.3em] text-[#A1A1AA] inline-flex items-center gap-2">
                    <Check size={13} /> Bearbeitet
                  </p>
                ) : (
                  <button
                    onClick={() => markHandled(o.id)}
                    data-testid={`admin-revocation-handle-${o.order_number}`}
                    className="btn-gold px-6 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light"
                  >
                    Als bearbeitet markieren
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// small inputs
// -------------------------------------------------------------------------
const TInput = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
      {label}
    </span>
    <input
      {...rest}
      data-testid={testid}
      className="mt-1 w-full bg-transparent border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60"
    />
  </label>
);

const TArea = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
      {label}
    </span>
    <textarea
      {...rest}
      rows={4}
      data-testid={testid}
      className="mt-1 w-full bg-transparent border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60 resize-none"
    />
  </label>
);
